// Chat files on the web (CHAT-FILES): the composer's quick path and the message's files.
//   - Staging: the paperclip, a drop anywhere on the conversation or thread panel, or a pasted
//     image uploads each file at once (its own request, so each shows its progress) as a staged
//     file; ✕ discards it; the message sends with the ready ones (text optional). A file that
//     cannot go (too big, too many, refused) stays on the strip with why — never skipped silently.
//   - A message's files: images as previews (a grid for several) that open full size in a
//     lightbox; other files as chips with name, size and download; HEIC says its preview is
//     unavailable. Every byte comes through the authenticated proxy (`attachments.content`) as a
//     blob URL. "Save to task" files an independent copy onto a task (a quick modal).
import { CHAT_FILE_LIMITS, FILE_LIMITS } from "@companyos/schema/contracts";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  Progress,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  ExternalLinkIcon,
  FileIcon,
  FileImageIcon,
  FolderInputIcon,
  UploadIcon,
  XIcon,
} from "lucide-react";
import {
  type DragEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type Api, apiUrl } from "../../api/client";
import { problemText } from "../ops-parts";
import { formatBytes } from "../work/files";
import type { MessageView } from "./chat-queries";
import { aspectOf, type ImageVariant, sizeQuery, useNearViewport, variantFor } from "./image-size";
import type { ChatContext } from "./use-chat";

export type ChatFile = MessageView["attachments"][number];

/** The largest file the API takes at all (a company's own cap may be lower; it says so). */
const CEILING_BYTES = FILE_LIMITS.maxUploadMbCeiling * 1024 * 1024;
const MAX_FILES = CHAT_FILE_LIMITS.maxPerMessage;
const HEIC = /\.(heic|heif)$/i;

const blobKey = (co: string, id: string, variant: ImageVariant) =>
  ["company", co, "chat", "file-blob", id, variant] as const;

/**
 * A chat file's bytes through the authenticated proxy: the original, or (IMAGE-PREVIEWS) its small
 * WebP copy (`size`), which the server keeps and caches for good.
 */
async function chatFileBlob(
  api: Api,
  co: string,
  id: string,
  o: { variant?: ImageVariant; download?: boolean } = {},
): Promise<Blob> {
  const size = sizeQuery(o.variant ?? "full");
  return (await api.client.attachments.content({
    params: { co, id },
    query: {
      ...(size ? { size } : {}),
      ...(o.download ? { download: "true" as const } : {}),
    },
  })) as Blob;
}

/** A blob URL of a chat file while the component lives (revoked after). */
export function useChatBlobUrl(api: Api, co: string, id: string | null, variant: ImageVariant) {
  const q = useQuery({
    queryKey: blobKey(co, id ?? "none", variant),
    queryFn: () => chatFileBlob(api, co, id as string, { variant }),
    enabled: id !== null,
    staleTime: 5 * 60_000,
    // Dropped soon after the message leaves the screen (deleted, or access gone).
    gcTime: 60_000,
    retry: false,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    // Another file (or none yet): never show the previous one's image under its name.
    if (!q.data) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(q.data);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [q.data]);
  return { url, pending: q.isPending && id !== null, error: q.isError };
}

export async function downloadChatFile(
  api: Api,
  co: string,
  f: Pick<ChatFile, "id" | "file_name">,
) {
  try {
    const blob = await chatFileBlob(api, co, f.id, { download: true });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = f.file_name;
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  } catch (e) {
    toast.add({
      title: `Could not download ${f.file_name}`,
      description: problemText(e, "It may have been deleted."),
      type: "error",
    });
  }
}

/** The original (full size, untouched) in a new tab. */
export async function openChatFileOriginal(
  api: Api,
  co: string,
  f: Pick<ChatFile, "id" | "file_name">,
) {
  try {
    const url = URL.createObjectURL(await chatFileBlob(api, co, f.id));
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    toast.add({
      title: `Could not open ${f.file_name}`,
      description: problemText(e, "It may have been deleted."),
      type: "error",
    });
  }
}

// Staging ----------------------------------------------------------------------------------------

export type StagedFile = {
  key: string;
  name: string;
  size: number;
  /** A local preview of an image (an object URL of the picked file). */
  previewUrl: string | null;
  heic: boolean;
  progress: number;
  state: "uploading" | "done" | "error";
  error?: string;
  view?: ChatFile;
  /**
   * CHAT-SWITCH-B (Codex r5 #3): a file of a draft put back from a refused send. The draft keeps
   * it, so leaving the conversation never discards it on the server; only its ✕ does (and tells
   * the draft, `onRemove`).
   */
  kept?: { onRemove: (id: string) => void };
};

/**
 * The upload URL on the API at `baseUrl`. Production builds with `VITE_API_URL=/` (same origin):
 * the trailing slash must go, or `//v1/…` is a protocol-relative URL to a host named `v1` and
 * every upload dies as a network error (CHAT-UPLOAD-FIX, 2026-10-27).
 */
export function chatUploadUrl(baseUrl: string, co: string): string {
  return apiUrl(baseUrl, `/v1/companies/${encodeURIComponent(co)}/chat/attachments`);
}

/** A failed upload's reason, in words a person can act on. */
export function uploadErrorText(
  status: number,
  problem: { detail?: string; title?: string; errors?: { message?: string }[] } | null,
): string {
  const said = problem?.detail ?? problem?.errors?.[0]?.message;
  if (status === 0) return "No connection to the server. Check your network and retry.";
  if (status === 401) return "You are signed out. Sign in again, then add the file again.";
  if (status === 403) return `Not allowed${said ? `: ${said}` : ": you cannot post files here"}`;
  if (status === 413)
    // the API's own words name the company's limit; a raw body cap or a proxy's 413 does not
    return said && !said.startsWith("body exceeds")
      ? said
      : `Too large (the limit is at most ${FILE_LIMITS.maxUploadMbCeiling} MB per file)`;
  if (status === 415) return `This file type is not accepted${said ? `: ${said}` : ""}`;
  if (status === 429) return "Too many uploads at once. Wait a moment and retry.";
  return said ?? problem?.title ?? `Upload failed (error ${status})`;
}

/** One upload with progress: XHR against the API (http), or the SDK (the in-memory API). */
export function uploadOne(
  api: Api,
  co: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<ChatFile> {
  const form = new FormData();
  // a paste may carry no name: give the part one (a nameless part is not a file to every parser)
  const named = file.name ? file : new File([file], "pasted image", { type: file.type });
  form.set("file", named, named.name);
  if (api.mode !== "http" || api.baseUrl === undefined) {
    onProgress(50);
    return api.client.attachments.upload({ params: { co }, body: form }) as Promise<ChatFile>;
  }
  const url = chatUploadUrl(api.baseUrl, co);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.withCredentials = true;
    xhr.responseType = "json";
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
    };
    xhr.onload = () => {
      if (xhr.status === 201) return resolve(xhr.response as ChatFile);
      // A proxy's refusal (Caddy's 413, a gateway's 502) has no JSON body: response is null.
      const p = xhr.response && typeof xhr.response === "object" ? xhr.response : null;
      reject(new Error(uploadErrorText(xhr.status, p)));
    };
    xhr.onerror = () => reject(new Error(uploadErrorText(0, null)));
    xhr.send(form);
  });
}

/**
 * The composer's staged files: `add` uploads each picked, dropped or pasted file at once;
 * `remove` is the ✕ (a done file is discarded on the server too); `take` hands the ready files
 * to a send and clears them (a failed one stays, with its reason, until removed).
 */
export function useStagedFiles(chat: Pick<ChatContext, "api" | "co">) {
  const { api, co } = chat;
  const [items, setItems] = useState<StagedFile[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const patch = useCallback(
    (key: string, p: Partial<StagedFile>) =>
      setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...p } : x))),
    [],
  );
  // Object URLs go with the strip, and so do its uploads: unsent files are discarded on the
  // server (they would otherwise count against the staging quota until they expire).
  const discard = useCallback(
    (id: string) => void api.client.attachments.discard({ params: { co, id } }).catch(() => {}),
    [api, co],
  );
  const unmounted = useRef(false);
  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
      for (const x of itemsRef.current) {
        if (x.previewUrl) URL.revokeObjectURL(x.previewUrl);
        if (x.view && !x.kept) discard(x.view.id);
      }
      // Uploads still in flight finish and are discarded then (`unmounted`): aborting one could
      // leave a file the server already stored with no id to discard.
    };
  }, [discard]);

  const add = useCallback(
    (files: File[]) => {
      const room = MAX_FILES - itemsRef.current.filter((x) => x.state !== "error").length;
      const taken = files.slice(0, Math.max(0, room));
      const left = files.slice(taken.length);
      if (left.length)
        toast.add({
          title: `At most ${MAX_FILES} files per message`,
          description: `Not added: ${left.map((f) => f.name || "pasted image").join(", ")}`,
          type: "error",
        });
      const fresh: StagedFile[] = taken.map((f) => {
        const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const heic = HEIC.test(f.name) || /image\/hei[cf]/.test(f.type);
        const image = f.type.startsWith("image/") && !heic;
        const tooBig = f.size > CEILING_BYTES;
        const empty = f.size === 0;
        return {
          key,
          name: f.name || "pasted image",
          size: f.size,
          previewUrl: image && !tooBig ? URL.createObjectURL(f) : null,
          heic,
          progress: 0,
          state: tooBig || empty ? "error" : "uploading",
          ...(tooBig ? { error: `Too large (over ${formatBytes(CEILING_BYTES)})` } : {}),
          ...(empty ? { error: "The file is empty" } : {}),
        };
      });
      itemsRef.current = [...itemsRef.current, ...fresh];
      setItems((xs) => [...xs, ...fresh]);
      fresh.forEach((s, i) => {
        const file = taken[i];
        if (!file || s.state !== "uploading") return;
        uploadOne(api, co, file, (pct) => patch(s.key, { progress: pct }))
          .then((view) => {
            // Removed (✕) or gone while it uploaded: the late upload is discarded, not kept.
            if (unmounted.current || !itemsRef.current.some((x) => x.key === s.key))
              return discard(view.id);
            patch(s.key, { state: "done", progress: 100, view });
          })
          .catch((e: unknown) => {
            patch(s.key, {
              state: "error",
              error: problemText(e, e instanceof Error ? e.message : "Upload failed"),
            });
          });
      });
    },
    [api, co, patch, discard],
  );

  /** `byUser`: the ✕ (a kept draft file goes from its draft too); else a strip being emptied. */
  const drop = useCallback(
    (key: string, byUser: boolean) => {
      const x = itemsRef.current.find((i) => i.key === key);
      if (!x) return;
      // (One still uploading leaves the strip now; its upload is discarded when it lands.)
      if (x.previewUrl) URL.revokeObjectURL(x.previewUrl);
      itemsRef.current = itemsRef.current.filter((i) => i.key !== key);
      setItems((xs) => xs.filter((i) => i.key !== key));
      if (x.kept && !byUser) return;
      if (x.kept && x.view) x.kept.onRemove(x.view.id);
      if (x.view) discard(x.view.id);
    },
    [discard],
  );
  const remove = useCallback((key: string) => drop(key, true), [drop]);

  /**
   * Empties the strip (another conversation opened in the same composer): all discarded, but a
   * draft's kept files (they come back with it).
   */
  const clear = useCallback(() => {
    for (const x of [...itemsRef.current]) drop(x.key, false);
  }, [drop]);

  // Codex r6 #2: a draft's kept files, by id, while a send has them (`take`): a send that fails
  // puts them back still kept (`putBack` with no `kept`), not as plain uploads.
  const keptTaken = useRef(new Map<string, NonNullable<StagedFile["kept"]>>());

  /** The ready files for a send; they leave the strip (failed ones stay, with their reason). */
  const take = useCallback((): ChatFile[] => {
    const ready = itemsRef.current.filter((x) => x.state === "done" && x.view);
    for (const x of ready) {
      if (x.previewUrl) URL.revokeObjectURL(x.previewUrl);
      if (x.kept && x.view) keptTaken.current.set(x.view.id, x.kept);
    }
    setItems((xs) => xs.filter((x) => !(x.state === "done" && x.view)));
    itemsRef.current = itemsRef.current.filter((x) => !(x.state === "done" && x.view));
    return ready.map((x) => x.view as ChatFile);
  }, []);

  /** A send that failed before its message existed: its files go back on the strip. */
  const putBack = useCallback(
    (files: readonly ChatFile[], kept?: { onRemove: (id: string) => void }) => {
      // Already on the strip (a draft shown again): not twice.
      const fresh = files.filter((f) => !itemsRef.current.some((x) => x.view?.id === f.id));
      if (fresh.length === 0) return;
      const back: StagedFile[] = fresh.map((view) => {
        const own = kept ?? keptTaken.current.get(view.id);
        keptTaken.current.delete(view.id);
        return {
          key: view.id,
          name: view.file_name,
          size: view.bytes,
          previewUrl: null,
          heic: view.preview_gap === "heic",
          progress: 100,
          state: "done",
          view,
          ...(own ? { kept: own } : {}),
        };
      });
      itemsRef.current = [...back, ...itemsRef.current];
      setItems((xs) => [...back, ...xs]);
    },
    [],
  );

  return {
    items,
    add,
    remove,
    take,
    putBack,
    clear,
    uploading: items.some((x) => x.state === "uploading"),
    ready: items.some((x) => x.state === "done"),
  };
}

export type StagedFiles = ReturnType<typeof useStagedFiles>;

/** The strip above the composer's text: thumbnails or chips, progress, ✕, and why one failed. */
export function StagedStrip({ staged }: { staged: StagedFiles }) {
  if (staged.items.length === 0) return null;
  return (
    <ul
      data-slot="staged-files"
      aria-label="Files to send"
      className="flex snap-x gap-2 overflow-x-auto overscroll-x-contain py-1"
    >
      {staged.items.map((x) => (
        <li
          key={x.key}
          data-slot="staged-file"
          data-state={x.state}
          className={cn(
            "relative flex h-14 max-w-56 min-w-0 shrink-0 snap-start items-center gap-2 rounded-lg border bg-card p-1.5 pr-7",
            x.state === "error" ? "border-destructive" : "border-border",
          )}
        >
          <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-sunken text-secondary">
            {x.previewUrl ? (
              <img src={x.previewUrl} alt="" className="size-full object-cover" />
            ) : x.heic ? (
              <FileImageIcon aria-hidden="true" className="size-4" />
            ) : (
              <FileIcon aria-hidden="true" className="size-4" />
            )}
          </span>
          <span className="min-w-0 leading-tight">
            <span className="block truncate text-xs font-medium text-foreground">{x.name}</span>
            {/* an error wraps to two lines (and its whole text is the tooltip): one truncated
                line showed only "Upload failed…" and hid the reason (CHAT-UPLOAD-FIX) */}
            <span
              title={x.state === "error" ? x.error : undefined}
              className={cn(
                "block text-[11px] tabular-nums",
                x.state === "error"
                  ? "line-clamp-2 text-danger-text"
                  : "truncate text-muted-foreground",
              )}
            >
              {x.state === "error"
                ? x.error
                : x.state === "uploading"
                  ? `Uploading ${x.progress}%`
                  : x.heic
                    ? "HEIC · no preview"
                    : formatBytes(x.size)}
            </span>
          </span>
          {x.state === "uploading" ? (
            <Progress
              value={x.progress}
              aria-label={`Uploading ${x.name}`}
              className="absolute inset-x-1.5 bottom-1 [&_[data-slot=progress-track]]:h-0.5"
            />
          ) : null}
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={`Remove ${x.name}`}
            className="absolute top-1 right-1"
            onClick={() => staged.remove(x.key)}
          >
            <XIcon />
          </Button>
        </li>
      ))}
    </ul>
  );
}

/** Whether a drag carries files (not text or a link being dragged around the page). */
const hasFiles = (e: DragEvent) => [...(e.dataTransfer?.types ?? [])].includes("Files");

/**
 * Drop files anywhere on the conversation (or the thread panel): an overlay says where they go
 * while files are dragged over it.
 */
export function FileDropZone({
  onFiles,
  disabled,
  label = "Drop files to attach",
  className,
  children,
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  label?: string;
  className?: string;
  children: ReactNode;
}) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: a file drop target; its keyboard twin is the composer's paperclip.
    <div
      data-slot="file-drop-zone"
      className={cn("relative", className)}
      onDragEnter={(e) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        depth.current += 1;
        setOver(true);
      }}
      onDragOver={(e) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={() => {
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setOver(false);
      }}
      onDrop={(e) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        const files = [...(e.dataTransfer.files ?? [])];
        if (files.length) onFiles(files);
      }}
    >
      {children}
      {over ? (
        <div
          data-slot="drop-overlay"
          className="pointer-events-none absolute inset-2 z-30 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary bg-background/85 text-sm font-medium text-foreground"
        >
          <UploadIcon aria-hidden="true" className="size-6 text-primary" />
          {label}
        </div>
      ) : null}
    </div>
  );
}

// A message's files ------------------------------------------------------------------------------

function ImageTile({
  chat,
  file,
  onOpen,
  single,
}: {
  chat: Pick<ChatContext, "api" | "co">;
  file: ChatFile;
  onOpen: () => void;
  single: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  // Only images in or near the view are fetched (the list can be long).
  const near = useNearViewport(ref);
  const listVariant = variantFor(file, "list");
  const small = useChatBlobUrl(chat.api, chat.co, near ? file.id : null, listVariant);
  // The small copy failed (or the file has none): the original.
  const full = useChatBlobUrl(
    chat.api,
    chat.co,
    near && listVariant !== "full" && small.error ? file.id : null,
    "full",
  );
  const url = small.url ?? full.url;
  // The stored size reserves the box before the bytes arrive, so the layout does not jump.
  const aspect = aspectOf(file);
  const boxed = single && aspect !== null;
  return (
    <button
      ref={ref}
      type="button"
      data-slot="message-image"
      data-variant={listVariant}
      aria-label={`Open ${file.file_name}`}
      onClick={onOpen}
      className={cn(
        "group/img relative overflow-hidden rounded-lg border border-border-subtle bg-sunken focus-visible:outline-2 focus-visible:outline-ring",
        single ? "max-h-72 max-w-full sm:max-w-sm" : "aspect-square w-full",
      )}
    >
      {boxed ? (
        // An empty, invisible image of the stored size holds the box (scaled like the real one)
        // until the bytes arrive; the real image then fills it.
        <img
          src={`data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${file.width}" height="${file.height}"/>`)}`}
          alt=""
          aria-hidden="true"
          width={file.width ?? undefined}
          height={file.height ?? undefined}
          className="invisible block h-auto max-h-72 w-auto max-w-full"
        />
      ) : null}
      {url ? (
        <img
          src={url}
          alt={file.file_name}
          loading="lazy"
          decoding="async"
          className={cn(
            boxed
              ? "absolute inset-0 size-full object-cover"
              : single
                ? "max-h-72 w-auto object-contain"
                : "size-full object-cover",
          )}
        />
      ) : (
        <span
          className={cn(
            "flex items-center justify-center",
            boxed ? "absolute inset-0" : "size-full min-h-24",
          )}
        >
          {!near ? null : small.pending || full.pending ? (
            <Spinner />
          ) : (
            <FileImageIcon className="size-5" />
          )}
        </span>
      )}
    </button>
  );
}

function FileChip({
  chat,
  file,
  onSave,
}: {
  chat: Pick<ChatContext, "api" | "co">;
  file: ChatFile;
  onSave?: (() => void) | undefined;
}) {
  return (
    <li
      data-slot="message-file"
      className="group/file flex h-12 max-w-full min-w-0 items-center gap-2 rounded-lg border border-border bg-card py-1 pr-1 pl-2 sm:max-w-72"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-sunken text-secondary">
        {file.preview_gap ? (
          <FileImageIcon aria-hidden="true" className="size-4" />
        ) : (
          <FileIcon aria-hidden="true" className="size-4" />
        )}
      </span>
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block truncate text-sm font-medium text-foreground">{file.file_name}</span>
        <span className="flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
          {formatBytes(file.bytes)}
          {file.preview_gap === "heic" ? (
            <>
              <span aria-hidden="true">·</span>
              <span data-slot="preview-gap">Preview unavailable (HEIC)</span>
              <InfoTip label="About HEIC">
                Browsers can't show HEIC photos. Download it to open it, or send it as JPG.
              </InfoTip>
            </>
          ) : null}
        </span>
      </span>
      <FileAction
        label={`Download ${file.file_name}`}
        tip="Download"
        onClick={() => void downloadChatFile(chat.api, chat.co, file)}
      >
        <DownloadIcon />
      </FileAction>
      {onSave ? (
        <FileAction label={`Save ${file.file_name} to a task`} tip="Save to task" onClick={onSave}>
          <FolderInputIcon />
        </FileAction>
      ) : null}
    </li>
  );
}

function FileAction({
  label,
  tip,
  onClick,
  children,
  className,
}: {
  label: string;
  tip: string;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            className={className}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}

/** The files of a message: an image grid (lightbox on click) and chips for the rest. */
export function MessageFiles({ chat, files }: { chat: ChatContext; files: readonly ChatFile[] }) {
  const images = files.filter((f) => f.preview === "image");
  const others = files.filter((f) => f.preview !== "image");
  const [open, setOpen] = useState<number | null>(null);
  const [saving, setSaving] = useState<ChatFile | null>(null);
  const canSave = chat.meId !== null && !chat.readOnly;
  return (
    <div data-slot="chat-files" className="mt-1 flex max-w-xl flex-col gap-1.5">
      {images.length ? (
        <div
          data-slot="image-grid"
          className={cn(
            images.length === 1 ? "flex" : "grid max-w-md grid-cols-2 gap-1.5 sm:grid-cols-3",
          )}
        >
          {images.map((f, i) => (
            <ImageTile
              key={f.id}
              chat={chat}
              file={f}
              single={images.length === 1}
              onOpen={() => setOpen(i)}
            />
          ))}
        </div>
      ) : null}
      {others.length ? (
        <ul className="flex flex-wrap gap-1.5">
          {others.map((f) => (
            <FileChip
              key={f.id}
              chat={chat}
              file={f}
              onSave={canSave ? () => setSaving(f) : undefined}
            />
          ))}
        </ul>
      ) : null}
      {open !== null && images[open] ? (
        <Lightbox
          chat={chat}
          images={images}
          index={open}
          onIndex={setOpen}
          onClose={() => setOpen(null)}
          onSave={canSave ? (f) => setSaving(f) : undefined}
        />
      ) : null}
      {saving ? (
        <SaveToTaskDialog chat={chat} file={saving} onClose={() => setSaving(null)} />
      ) : null}
    </div>
  );
}

function Lightbox({
  chat,
  images,
  index,
  onIndex,
  onClose,
  onSave,
}: {
  chat: ChatContext;
  images: readonly ChatFile[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
  onSave?: ((f: ChatFile) => void) | undefined;
}) {
  const file = images[index] as ChatFile;
  // The ~1600 px preview; the original only when there is no preview (or it failed).
  const previewVariant = variantFor(file, "lightbox");
  const preview = useChatBlobUrl(chat.api, chat.co, file.id, previewVariant);
  const original = useChatBlobUrl(
    chat.api,
    chat.co,
    previewVariant !== "full" && preview.error ? file.id : null,
    "full",
  );
  const full = {
    url: preview.url ?? original.url,
    error: preview.error && (previewVariant === "full" || original.error),
  };
  const many = images.length > 1;
  const step = (d: number) => onIndex((index + d + images.length) % images.length);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="sm:max-w-4xl"
        data-slot="chat-lightbox"
        onKeyDown={(e) => {
          if (!many) return;
          if (e.key === "ArrowRight") step(1);
          if (e.key === "ArrowLeft") step(-1);
        }}
      >
        <DialogHeader>
          <DialogTitle className="truncate pr-8">{file.file_name}</DialogTitle>
          <DialogDescription>
            {formatBytes(file.bytes)}
            {many ? ` · ${index + 1} of ${images.length}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="relative flex min-h-40 items-center justify-center">
          {full.url ? (
            <img
              src={full.url}
              alt={file.file_name}
              className="max-h-[70vh] max-w-full rounded-md object-contain"
            />
          ) : full.error ? (
            <p className="text-sm">The image could not be read.</p>
          ) : (
            <Spinner />
          )}
          {many ? (
            <>
              <Button
                variant="secondary"
                size="icon-sm"
                aria-label="Previous image"
                className="absolute left-1"
                onClick={() => step(-1)}
              >
                <ChevronLeftIcon />
              </Button>
              <Button
                variant="secondary"
                size="icon-sm"
                aria-label="Next image"
                className="absolute right-1"
                onClick={() => step(1)}
              >
                <ChevronRightIcon />
              </Button>
            </>
          ) : null}
        </div>
        <DialogFooter>
          {onSave ? (
            <Button variant="outline" onClick={() => onSave(file)}>
              <FolderInputIcon aria-hidden="true" />
              Save to task
            </Button>
          ) : null}
          <Button
            variant="outline"
            onClick={() => void openChatFileOriginal(chat.api, chat.co, file)}
          >
            <ExternalLinkIcon aria-hidden="true" />
            Open original
          </Button>
          <Button onClick={() => void downloadChatFile(chat.api, chat.co, file)}>
            <DownloadIcon aria-hidden="true" />
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** "Save to task": an independent copy on a task the person picks (search), in a folder. */
function SaveToTaskDialog({
  chat,
  file,
  onClose,
}: {
  chat: ChatContext;
  file: ChatFile;
  onClose: () => void;
}) {
  const { api, co } = chat;
  const [q, setQ] = useState("");
  const [task, setTask] = useState<{ id: string; title: string } | null>(null);
  const [folder, setFolder] = useState("From chat");
  const [busy, setBusy] = useState(false);
  const typed = q.trim();
  const hits = useQuery({
    queryKey: ["company", co, "chat", "save-task-search", typed],
    queryFn: () => api.client.search.palette({ params: { co }, query: { q: typed, per_kind: 10 } }),
    enabled: typed.length >= 2,
    staleTime: 30_000,
  });
  const tasks = useMemo(
    () => (hits.data?.data ?? []).filter((h) => h.kind === "task").slice(0, 8),
    [hits.data],
  );
  const save = async () => {
    if (!task) return;
    setBusy(true);
    try {
      await api.client.attachments.save({
        params: { co, id: file.id },
        body: { task_id: task.id, ...(folder.trim() ? { folder_path: folder.trim() } : {}) },
      });
      toast.add({ title: `Saved ${file.file_name} to “${task.title}”`, type: "success" });
      onClose();
    } catch (e) {
      toast.add({
        title: "Could not save it",
        description: problemText(e, "You may not be able to edit that task."),
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-slot="save-to-task">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1.5">
              Save to task
              <InfoTip label="About saving">
                Saves a separate copy of {file.file_name} in the task's files, noting who sent it
                and when. Deleting the chat message never removes the copy.
              </InfoTip>
            </DialogTitle>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="save-task-q">Task</FieldLabel>
            <Input
              id="save-task-q"
              autoFocus
              placeholder="Search tasks"
              value={task ? task.title : q}
              onChange={(e) => {
                setTask(null);
                setQ(e.target.value);
              }}
            />
            {!task && typed.length >= 2 ? (
              <ul className="mt-1 max-h-48 overflow-y-auto rounded-md border border-border-subtle">
                {hits.isPending ? (
                  <li className="p-2">
                    <Spinner />
                  </li>
                ) : tasks.length ? (
                  tasks.map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        className="flex w-full flex-col items-start px-2 py-1.5 text-left text-sm hover:bg-muted"
                        onClick={() => setTask({ id: t.id, title: t.title })}
                      >
                        <span className="truncate">{t.title}</span>
                        {t.subtitle ? (
                          <span className="truncate text-xs text-muted-foreground">
                            {t.subtitle}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="p-2 text-sm text-muted-foreground">No tasks match.</li>
                )}
              </ul>
            ) : null}
          </Field>
          <Field>
            <FieldLabel htmlFor="save-task-folder">Folder</FieldLabel>
            <Input
              id="save-task-folder"
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!task || busy}>
              Save copy
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
